import { BlogPost } from '../types';

const post: BlogPost = {
	slug: 'dora-gia-ton-megalo-adelfo-i-tin-megali-adelfi',
	title: 'Δώρα για τον Μεγάλο Αδελφό ή τη Μεγάλη Αδελφή (που Βοηθούν Πραγματικά)',
	description:
		'Τι χαρίζεις σε ένα μεγαλύτερο παιδί όταν έρχεται ένα νέο μωρό, γιατί ένα δώρο «μεγάλου αδελφού» έχει μεγαλύτερη σημασία απ’ όσο ακούγεται, και πώς να το χρονίσεις.',
	date: '2026-03-11',
	heroImage: '/assets/hero/kid-2/classic_scene.png',
	tags: ['οδηγοί δώρων', 'νέο αδελφάκι'],
	body: `Όταν έρχεται ένα νέο μωρό, το μεγαλύτερο παιδί παίρνει μια περίεργη μερίδα. Το σπίτι γεμίζει επισκέπτες, και σχεδόν όλη η προσοχή πηγαίνει σε κάποιον που δεν μπορεί ακόμα να κρατήσει το κεφάλι του όρθιο.

Ένα δώρο για το μεγαλύτερο παιδί αυτή τη στιγμή δεν αφορά πραγματικά το αντικείμενο. Αφορά το να του δοθεί ένας ρόλος.

## Γιατί αξίζει να προγραμματίσεις ένα δώρο «μεγάλου αδελφού»

Τα παιδιά ανάμεσα σε δύο και έξι χρονών γενικά καταλαβαίνουν τι συμβαίνει και δεν έχουν καμία στρατηγική για να το διαχειριστούν. Δεν εκτοπίζονται πραγματικά με κανέναν ουσιαστικό τρόπο, αλλά δεν είναι παράλογο να το διαβάζουν έτσι από όσα βλέπουν μπροστά τους.

Το να δώσεις στο μεγαλύτερο παιδί κάτι που το ονομάζει — *μεγάλος αδελφός*, *μεγάλη αδελφή* — αλλάζει το πλαίσιο του γεγονότος. Δεν είναι το άτομο από το οποίο παίρνονται πράγματα· είναι το άτομο που μόλις προήχθη.

Αυτό δουλεύει καλύτερα όταν το δώρο είναι συγκεκριμένο και όχι γενικό. Ένα μπλουζάκι που γράφει «Μεγάλος Αδελφός» είναι εντάξει. Ένα βιβλίο όπου το μεγαλύτερο παιδί είναι ο εικονογραφημένος ήρωας, και το μωρό είναι δευτερεύων χαρακτήρας στη *δική του* ιστορία, κάνει πολύ περισσότερα.

## Τι να χαρίσεις

**Ένα προσωποποιημένο βιβλίο που το βάζει στον ρόλο του μεγάλου αδελφού.** Η ισχυρότερη επιλογή. Δίνει στο παιδί μια αφήγηση στην οποία έχει ήδη έναν ρόλο, και διαβάζεται ξανά και ξανά — κάτι που έχει σημασία, γιατί αυτό δεν είναι ένα συναίσθημα που λύνεται σε ένα απόγευμα.

**Ένα προσωποποιημένο βιβλίο ζωγραφικής.** Χρήσιμο για το συγκεκριμένο πρόβλημα της περιόδου του νεογέννητου: μεγάλα διαστήματα όπου ο γονιός είναι απασχολημένος και το μεγαλύτερο παιδί χρειάζεται κάτι απορροφητικό που δεν χρειάζεται επίβλεψη.

**Ένα κειμήλιο και με τα δύο παιδιά.** Είναι περισσότερο δώρο για τους γονείς, αλλά αντέχει στον χρόνο για δεκαετίες.

**Κάτι που είναι μόνο δικό του.** Μια κούπα, μια τσάντα, ένα μπλουζάκι με τον δικό του χαρακτήρα. Το θέμα είναι ότι το μωρό δεν έχει κανένα δικαίωμα πάνω του. Τα τρίχρονα το παίρνουν αυτό πολύ σοβαρά.

## Τι να αποφύγεις

- **Οτιδήποτε θα κληρονομήσει τελικά το μωρό.** Υπονομεύει όλο το μήνυμα.
- **Δώρα που παρουσιάζονται ως «από το μωρό».** Τα μεγαλύτερα παιδιά το καταλαβαίνουν αμέσως και συνήθως εκλαμβάνεται ως ελαφρώς προσβλητικό.
- **Παιχνίδια που χρειάζονται γονιό για να λειτουργήσουν.** Ο χρόνος του γονιού τις πρώτες εβδομάδες είναι ο πιο σπάνιος πόρος στο σπίτι.

## Χρονισμός

Δώσε το τη μέρα που το μωρό έρχεται σπίτι, ή στην πρώτη επίσκεψη στο νοσοκομείο. Αυτή είναι η στιγμή που το μεγαλύτερο παιδί συνειδητοποιεί πιο έντονα την αλλαγή.

Αυτό σημαίνει ότι πρέπει να παραγγείλεις εκ των προτέρων. Τα προσωποποιημένα αντικείμενα φτιάχνονται κατά παραγγελία — δημιουργία εικονογράφησης, η δική σου έγκριση, εκτύπωση, αποστολή — οπότε άφησε δύο με τρεις εβδομάδες. Δεδομένου ότι οι ημερομηνίες τοκετού είναι κατά προσέγγιση, το να παραγγείλεις έναν μήνα νωρίτερα είναι λογικό.

Αν είσαι επισκέπτης συγγενής και όχι γονιός: φέρε κάτι για το μεγαλύτερο παιδί. Όλοι οι υπόλοιποι θα φέρουν κάτι για το μωρό, και το μεγαλύτερο παιδί θα μετράει.

## Μια σημείωση για το δεύτερο παιδί

Αν το κάνεις αυτό για το πρώτο παιδί, σχεδίασε να το κάνεις και για το δεύτερο σε λίγα χρόνια. Τα παιδιά κρατάνε αυτόν τον λογαριασμό με αξιοσημείωτη ακρίβεια, και το «υπάρχει βιβλίο για σένα και όχι για μένα» είναι ένα παράπονο με μακρά διάρκεια ζωής.`,
	faqs: [
		{
			question: 'Τι χαρίζεις σε ένα μεγαλύτερο παιδί όταν φτάνει ένα νέο μωρό;',
			answer:
				'Κάτι που είναι αδιαμφισβήτητα δικό του και που ονομάζει τον νέο του ρόλο. Ένα προσωποποιημένο βιβλίο που βάζει το μεγαλύτερο παιδί στον ρόλο του μεγάλου αδελφού ή της μεγάλης αδελφής δουλεύει ιδιαίτερα καλά, γιατί του δίνει έναν αφηγηματικό ρόλο και όχι απλώς μια ετικέτα, και μπορεί να ξαναδιαβαστεί τις εβδομάδες που ακολουθούν.',
		},
		{
			question: 'Πότε πρέπει να δώσεις ένα δώρο «μεγάλου αδελφού»;',
			answer:
				'Τη μέρα που το μωρό έρχεται σπίτι, ή στην πρώτη επίσκεψη στο νοσοκομείο — τη στιγμή που το μεγαλύτερο παιδί συνειδητοποιεί πιο έντονα την αλλαγή. Επειδή τα προσωποποιημένα αντικείμενα φτιάχνονται κατά παραγγελία, σχεδίασε να παραγγείλεις περίπου έναν μήνα νωρίτερα, δεδομένου ότι οι ημερομηνίες τοκετού είναι κατά προσέγγιση.',
		},
		{
			question: 'Πρέπει το δώρο «μεγάλου αδελφού» να παρουσιάζεται ως δώρο από το μωρό;',
			answer:
				'Συνήθως όχι. Τα μεγαλύτερα παιδιά συνήθως το καταλαβαίνουν, και μπορεί να εκληφθεί ως συγκαταβατικό. Δουλεύει καλύτερα όταν παρουσιάζεται ευθέως από τον ενήλικα που το χαρίζει.',
		},
	],
	lang: 'el',
	dateLocale: 'el-GR',
	ui: {
		homeLabel: 'Αρχική',
		blogLabel: 'Ιστολόγιο',
		faqHeading: 'Συχνές ερωτήσεις',
		moreReadingHeading: 'Περισσότερα άρθρα',
		ctaLabel: 'Δείτε τα προσωποποιημένα δώρα',
	},
};

export default post;
